import { parsePlaceholderExpression } from './parse-placeholder-expression';
import type { PlaceholderExpression } from './parse-placeholder-expression';
import { PLACEHOLDER_TOKEN_PATTERN } from './placeholder-token';
import { PLACEHOLDER_VALUES_PROP } from './placeholder-values';
import type { PlaceholderValues } from './placeholder-values';
import { warnPlaceholderOnce } from './placeholder-warnings';

const PLACEHOLDER_TOKEN = new RegExp(PLACEHOLDER_TOKEN_PATTERN, 'g');

type PathLookup = { found: true; value: unknown } | { found: false };

const NOT_FOUND: PathLookup = { found: false };

/**
 * Follows a path through the host's values without ever throwing.
 *
 * Only own properties count, so a path cannot wander onto a prototype —
 * `{{ user.constructor }}` finds nothing — and only objects and arrays are
 * stepped into. A getter or proxy that throws counts as not found rather than
 * taking the render down with it.
 */
const lookUpPath = (values: PlaceholderValues, path: readonly string[]): PathLookup => {
  try {
    let current: unknown = values;
    for (const key of path) {
      if (
        typeof current !== 'object' ||
        current === null ||
        !Object.prototype.hasOwnProperty.call(current, key)
      ) {
        return NOT_FOUND;
      }
      current = (current as Record<string, unknown>)[key];
    }
    return { found: true, value: current };
  } catch {
    return NOT_FOUND;
  }
};

/**
 * What a found value prints as, or `undefined` when it has no sensible text:
 * `null`, an object or array, a function, `NaN`. `0` and `false` print.
 */
const printValue = (value: unknown): string | undefined => {
  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
      return Number.isFinite(value) ? String(value) : undefined;
    case 'boolean':
      return String(value);
    default:
      return undefined;
  }
};

const describeValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return 'an array';
  }
  if (typeof value === 'object') {
    return 'an object';
  }
  return typeof value === 'number' ? String(value) : `a ${typeof value}`;
};

/**
 * The text one placeholder prints from values the host did pass, warning in
 * development when its path finds nothing usable — usually a typo such as
 * `lastname` for `lastName`. A `null` is the host saying "no value", so it
 * falls back without a warning.
 */
const printFromValues = (
  values: PlaceholderValues,
  expression: PlaceholderExpression,
  token: string
): string | undefined => {
  const lookup = lookUpPath(values, expression.path);
  if (!lookup.found) {
    warnPlaceholderOnce(
      `RuleCMS: ${token} found nothing in ${PLACEHOLDER_VALUES_PROP}. Paths are case-sensitive.`
    );
    return undefined;
  }

  const printed = printValue(lookup.value);
  if (printed === undefined && lookup.value !== null && lookup.value !== undefined) {
    warnPlaceholderOnce(
      `RuleCMS: ${token} found ${describeValue(lookup.value)} in ${PLACEHOLDER_VALUES_PROP}, ` +
        'which prints as nothing. Point it at a string, number or boolean.'
    );
  }
  return printed;
};

/**
 * Replaces every `{{ … }}` placeholder in `text` with what its path finds in
 * `values`, as plain text.
 *
 * A placeholder whose path finds nothing printable — nothing there, `null`, an
 * empty string, an object — shows its `??` fallback, or nothing without one. A
 * token that is not a valid placeholder stays exactly as written.
 *
 * `values` is `undefined` when the host passed none, which is the normal case
 * wherever placeholders are not in use: every placeholder then falls back,
 * silently.
 */
export function fillPlaceholders(text: string, values: PlaceholderValues | undefined): string {
  return text.replace(PLACEHOLDER_TOKEN, (token) => {
    const expression = parsePlaceholderExpression(token.slice(2, -2));
    if (expression === undefined) {
      return token;
    }

    const printed =
      values === undefined ? undefined : printFromValues(values, expression, token);
    return printed === undefined || printed === '' ? (expression.fallback ?? '') : printed;
  });
}
